import React from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import Layout from './components/Layout'
import GuardedRoute from './components/route-guards/GuardedRoute'
import RootContainer from './modules/RootContainer.layout'
import { FeatureFlagRoles, ReportRoles } from './utils/useRoleCheck'
import { CenteredProgress } from './spark_libs_deprecated/CenteredProgress'
import { Alert } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { userAgentParser } from '@edge/spark/utils'

const isMobileDevice = () => {
    const deviceType = userAgentParser.getDevice().type
    return deviceType === 'mobile' || deviceType === 'tablet'
}

interface Routes {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Component: React.LazyExoticComponent<any>
    path: string
    allowNotExact?: boolean
    /** noAuth routes are visible only to users that are not logged in. */
    noAuth?: boolean
    /** If a user needs only one required role set 'partialRoleList: true'
     *
     *  If 'partialRoleList: false' or not added then a user will need all roles in 'requiredRoles' array
     *
     *  If the route is unrestricted, do not set this parameter.
     */
    requiredRoles?: string[]
    partialRoleList?: boolean
    strict?: boolean
    /** Set to true if only Auction Edge admins (globalAdmin: true) are allowed access*/
    requireGlobalAdmin?: boolean
}

export const routeList: Routes[] = [
    {
        Component: React.lazy(() => import('./Views/AgentDetailPage')),
        path: '/details/agents',
        allowNotExact: true,
    },
    {
        Component: React.lazy(() => import('./Views/BidderStatsDetail')),
        path: '/bidderstats',
    },
    {
        Component: React.lazy(() => import('./Views/CompanyDetailPage')),
        path: '/details/companies',
        allowNotExact: true,
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/conditionreport',
    },
    {
        Component: React.lazy(() =>
            import('./spark_libs_deprecated/feature-dashboards').then((m) => ({ default: m.HomepageWrapper })),
        ),
        path: '/',
    },
    {
        Component: React.lazy(() =>
            import('./spark_libs_deprecated/feature-dashboards').then((m) => ({ default: m.HomepageWrapper })),
        ),
        path: '/dashboard',
    },
    {
        Component: React.lazy(() => import('./Views/MyContactLogFollowupsDetail')),
        path: '/myContactLogFollowups',
    },
    {
        Component: React.lazy(() => import('./Views/RecentAccountsDetail')),
        path: '/recentaccounts',
    },
    {
        Component: React.lazy(() => import('./Views/SalesOverviewDetail')),
        path: '/salesoverview',
    },
    {
        Component: React.lazy(() => import('./Views/SearchResults')),
        path: '/search',
        allowNotExact: true,
    },
    {
        Component: React.lazy(() => import('./Views/VDPViewsDetail')),
        path: '/vdpviews',
    },
    {
        Component: React.lazy(() => import('./Views/VehicleDetailPage')),
        path: '/details/vehicles',
        allowNotExact: true,
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/vehiclescheckedin',
    },
    {
        Component: React.lazy(() => import('./Views/SearchContactLogDetail')),
        path: '/searchdetails/customcard/contact-log',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/vehicles-without-photos',
    },
    {
        Component: React.lazy(() => import('./UnauthenticatedViews/ForceChangePassword')),
        path: '/forcechangepassword',
        noAuth: true,
    },
    {
        Component: React.lazy(() => import('./Views/AuctionReports')),
        path: '/auction/reports',
        requiredRoles: [ReportRoles.BASIC, ReportRoles.FINANCIALS],
        partialRoleList: true,
    },
    {
        Component: React.lazy(() => import('./Views/AuctionReportsV2')),
        path: '/auction/reportsv2',
        requiredRoles: [ReportRoles.BASIC, ReportRoles.FINANCIALS],
        partialRoleList: true,
    },
    {
        Component: React.lazy(() =>
            import('./spark_libs_deprecated/feature-dashboards').then((m) => ({ default: m.HomepageWrapper })),
        ),
        path: '/dashboards/:dashId',
    },
    {
        Component: React.lazy(() => import('./Views/FavoritesDetail')),
        path: '/favorites',
    },
    {
        Component: React.lazy(() => import('./Views/IFOffersDetail')),
        // TODO Need to add options for users to customize the view/path
        path: '/ifoffers',
    },
    {
        // Keep search and hash with redirect. e.g. `/simplescan?id=1234#hash` -> `/scanner?id=1234#hash`
        // @ts-expect-error wrong type?
        Component: ({ location }) =>
            isMobileDevice() ? (
                <Redirect to={{ ...location, pathname: '/documents/list' }} />
            ) : (
                <Redirect to={{ ...location, pathname: '/scanner' }} />
            ),
        path: '/simplescan',
    },
    {
        // Lazy named import syntax: https://github.com/facebook/react/issues/14603
        // @ts-expect-error wrong type?
        Component: isMobileDevice()
            ? () => <Redirect to={{ pathname: '/documents/list' }} />
            : React.lazy(() => import('@edge/spark/feature-docs').then((m) => ({ default: m.DocsScanner }))),
        path: '/scanner',
    },
    {
        Component: React.lazy(() => import('./Views/OdsIFOffersDetail')),
        path: '/odsifoffers',
    },
    {
        Component: React.lazy(() =>
            import('./Views/PostSaleInspectionsPlus').then((m) => ({ default: m.PostSaleInspectionsPlus })),
        ),
        path: '/postsaleinspections-plus',
        allowNotExact: true,
    },
    {
        Component: React.lazy(() => import('./Views/PostSaleInspections')),
        path: '/postsaleinspections',
        allowNotExact: true,
        requiredRoles: [FeatureFlagRoles.PSI_MODULE],
    },
    /** Vehicle Listings Paths */
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/vehicle-ci',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/by-buyer',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/by-account',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/customcard/vehicle-deals',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/customcard/vehicle-buyers',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/customcard/vehicle-sellers',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/customcard/vehicle-presale',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/customcard/vehicle-dealer',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/watchedvehicles',
    },
    /** Acount Listings Paths */
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/account-vehicles-inventory',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/account-vehicles-consigned',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/customcard/account-vehicles-sold',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/customcard/account-vehicles-bought',
    },
    {
        Component: React.lazy(() => import('./Views/SearchVehiclesDetailNew')),
        path: '/searchdetails/customcard/account-vehicles-notitles',
    },
    {
        Component: React.lazy(() =>
            import('./Views/DocumentsListView').then((m) => ({ default: m.DocumentsListView })),
        ),
        path: '/documents/list',
        requiredRoles: [FeatureFlagRoles.VIEW_AND_MANAGE_DOCUMENTS],
    },
    // {
    //     Component: React.lazy(() => import('./Views/Audit')),
    //     path: '/audit',
    //     requireGlobalAdmin: true,
    // },
]

const ViewRouter: React.FC = () => {
    const { releaseSparkMaintenanceBanner } = useFlags()

    return (
        <Layout>
            {releaseSparkMaintenanceBanner && (
                <Alert sx={{ textAlign: 'center', alignItems: 'center' }} variant="outlined" severity="warning">
                    Edge Spark is currently performing maintenance. You may experience limited functionality while using
                    the site. If you have any questions, please{' '}
                    <a target="_blank" rel="noopener noreferrer" href="mailto:support@auctionedge.com">
                        contact us
                    </a>
                </Alert>
            )}

            <React.Suspense fallback={<CenteredProgress />}>
                <Switch>
                    {/* @deprecated */}
                    {routeList
                        .map(
                            ({
                                Component,
                                path,
                                noAuth,
                                allowNotExact,
                                requiredRoles,
                                partialRoleList,
                                strict,
                                requireGlobalAdmin,
                            }) => {
                                return noAuth ? (
                                    <Route
                                        component={Component}
                                        exact={!allowNotExact}
                                        key={path}
                                        path={path}
                                        strict={strict}
                                    />
                                ) : (
                                    <GuardedRoute
                                        component={Component}
                                        exact={!allowNotExact}
                                        key={path}
                                        path={path}
                                        strict={strict}
                                        requiredRoles={requiredRoles}
                                        partialRoleList={partialRoleList}
                                        requireGlobalAdmin={requireGlobalAdmin}
                                    />
                                )
                            },
                        )
                        .filter((res) => res !== null)}

                    {/* new routing structure */}
                    <RootContainer />
                </Switch>
            </React.Suspense>
        </Layout>
    )
}

export default ViewRouter
